import React from "react";
import BaseModel from "../models/BaseModel";
import type Channel from "../models/Channel";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import { ChannelType, channelTypeInfo, getChannelTypePicture } from "../models/ChannelType";
import Connection from "../models/Connection";
import type Page from "../models/Page";
import type ConnectableObject from "../models/ConnectableObject";
import AllowedConnections from "../models/AllowedConnections";
import { isUiModeDeveloper, isUiModeExpert } from "../uiMode";
import Device from "../models/Device";
import ObjectReference from "../components/ObjectReference";

import Logger from "../Logger";
import EditableText from "../components/EditableText";
import { itx } from "../translationItx/itx";
const logger = new Logger();

export default class ChannelTreeViewModel extends TreeViewModel {
  get typedObj(): Channel {
    return this.obj as Channel;
  }

  get isFolder(): boolean {
    if (!isUiModeExpert()) {
      return false;
    }

    if (channelTypeInfo(this.typedObj.channelType).isSupply) {
      return false;
    }

    return true;
  }

  // get iconUrl(): string | undefined {
  //    return `las la-rotate-90 la-tablet`;
  // }

  getIcon(context?: any): any | undefined {
    if (channelTypeInfo(this.typedObj.channelType).isSupply) {
      return undefined;
    }

    const makeNameEditable = context == undefined || context != "ObjectReference";

    let t = getChannelTypePicture(this.typedObj);
    let c = this.typedObj.className == "ButtonChannel" ? "buttonChannelTypeImage" : "channelTypeImage"; // no itx

    let textElem;

    const knxChannel = this.typedObj.knxChannel;
    const style: any = {};
    let error = this.typedObj.errorText || this.typedObj.hasErrors;

    if (knxChannel) {
      if (knxChannel.channelType == ChannelType.Environment) {
        textElem = (
          <div
            style={{
              whiteSpace: "nowrap",
              display: "flex",
              flexDirection: "row",
              color: "grey",
              alignItems: "center",
            }}
          >
            &nbsp;{itx("channelType.Environment", "Environment")}
          </div>
        );
      } else {
        textElem = (
          <div
            style={{
              whiteSpace: "nowrap",
              display: "flex",
              flexDirection: "row",
              color: "grey",
              alignItems: "center",
              height: "100%",
            }}
          >
            <div
            // className="nav_C2"
            >
              &nbsp;{this.typedObj.getChannelNameForUI()}:&nbsp;
            </div>

            <EditableText
              obj={this.typedObj}
              disableEdit={!makeNameEditable}
              // className="nav_C2"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "140px",
                paddingLeft: "0px",
                paddingRight: "0px",
              }}
              property={"nameOrConnectedNames"}
            />
          </div>
        );
      }
    } else {
      if ((this.typedObj.parent as Device)?.template) {
        error = true;

        textElem = (
          <div
            style={{
              whiteSpace: "nowrap",
              display: "flex",
              flexDirection: "row",
              color: "grey",
            }}
          >
            &nbsp; {this.typedObj.getChannelNameForUI()}
          </div>
        );
      } else {
        textElem = (
          <div
            style={{
              whiteSpace: "nowrap",
              display: "flex",
              flexDirection: "row",
              color: "grey",
            }}
          >
            &nbsp;
            {
              <EditableText
                obj={this.typedObj}
                property="name"
                disableEdit={!this.typedObj.selected && !makeNameEditable}
              />
            }
          </div>
        );
      }
    }

    if (this.typedObj.isStatusChannel) {
      style.backgroundColor = "var(--isStatusColor)"; // no itx
    }
    if (error) {
      style.backgroundColor = "var(--backgroundColorError)"; // no itx
    }

    if (this.typedObj.className == "ButtonChannel") {
      c += " textButton"; // no itx
    } else {
      c += " textActor"; // no itx
    }

    c += " nav_C1"; // no itx

    if (error) {
      let tooltipText = state.translate(
        "treeViewModel.configureChannel",
        "Konfiguriere einen Kanal in den Parametern!",
      );

      if (isUiModeDeveloper()) {
        tooltipText = `channel: ${this.typedObj.tag} ${this.typedObj.channelType}`;
      }

      return (
        <div
          className={c}
          style={style}
          data-tooltip-id="tv-tooltip"
          data-tooltip-content={tooltipText}
          data-tooltip-place="bottom-end" // no itx
          data-tooltip-variant="error"
        >
          {t && <img src={t} />} {textElem}{" "}
        </div>
      );
    } else {
      return (
        <div className={c} style={style}>
          {t && <img src={t} />} {textElem}{" "}
        </div>
      );
    }
  }

  getTagNeeded(): boolean {
    return false;
  }

  getNameNeeded(): boolean {
    return this.typedObj.isButtonChannel;
  }

  getDisableEdit(): boolean {
    return this.typedObj.className != "ButtonChannel";
  }

  get childCollection(): Array<BaseModel> | undefined {
    if (this.typedObj.signals.size == 0 && this.typedObj.mappedChannel) {
      const res = this.typedObj.mappedChannel.signals.sortIntBy("number");
      return res;
    } else {
      const res = this.typedObj.signals.sortIntBy("number");
      return res;
    }
  }

  // alertNoKnxChannel(): any | undefined {
  //   if (!this.typedObj.mappedChannel && (this.typedObj.parent as Device)?.template) {
  //     const imgSrcAlert = "images/alert.png";
  //     return (
  //       <img
  //         src={imgSrcAlert}
  //         data-tooltip-id="tv-tooltip"
  //         data-tooltip-content={state.translate(
  //           "treeViewModel.configureChannel",
  //           "Konfiguriere einen Kanal in den Parametern!",
  //         )}
  //         data-tooltip-place="right"
  //         data-tooltip-variant="error"
  //         style={{
  //           width: "25px",
  //           height: "25px",
  //           marginRight: "10px",
  //           position: "relative",
  //         }}
  //       />
  //     );
  //   } else {
  //     return undefined;
  //   }
  // }

  //
  // Standart: Show Name
  //
  getFullTitle(isExpanded: boolean): string | undefined | JSX.Element {
    return " ";

    // if (channelTypeInfo(this.typedObj.channelType).isSupply) {
    //   const consumer = this.typedObj.getConnectedConsumer();
    //   if (consumer) {
    //     return (
    //       <div style={{ display: "flex", flexDirection: "row" }}>
    //         <div className="nav_C1">{consumer?.getRelativeTag(this.typedObj.parent as Device)}</div>
    //         <div className="nav_C2" style={{ marginLeft: "10px" }}>
    //           {consumer?.name}
    //         </div>
    //       </div>
    //     );
    //   }
    //   return "??";
    // }

    // if (this.typedObj.isButtonChannel && this.typedObj.name.trim() != "") {
    //   return (this.typedObj as ButtonChannel).name;
    // }

    // return undefined;
  }

  getTitlePostfix(): string | JSX.Element {
    // if (channelTypeInfo(this.typedObj.channelType).isSupply) {
    //   return "";
    // }

    return this.connectionsElement();
  }

  // getPropertyControl(setPinned: (pinned: boolean) => void): any {
  //   if (this.typedObj.isButtonChannel) {
  //     return undefined;
  //   }

  //   if (channelTypeInfo(this.typedObj.channelType).isSupply) {
  //     return undefined;
  //   }

  //   return <ChannelPropertyView obj={this.obj as ConnectableObject} setPinned={setPinned} />;
  // }

  ///////////////////////////////////////////////////////////////////////////

  allowDrop(event: any): boolean {
    return true;
  }

  allowDrag(): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (channelTypeInfo(this.typedObj.channelType).isSupply) {
      return new DropResult(
        false,
        state.translate("channelTreeViewModel.connectionForbidden", "Verbindung ist nicht erlaubt"),
      );
    }

    if (obj.isConsumer || obj.isMultiplexer || obj.isScene) {
      let isConnectable = true;
      const target = this.typedObj;
      const source = obj as ConnectableObject;
      // sort actorChannel which are allready connected out

      if (target.isActorChannel && target.getConnectedConsumer()) {
        return new DropResult(
          false,
          state.translate("channelTreeViewModel.connectionForbidden", "Verbindung ist nicht erlaubt"),
        );
      }

      //
      // try to to automaticly set channelType
      // for customized Buttons: zb BE-GT20x.02
      //
      this.typedObj.autoSetChannelType(source.channelType);

      isConnectable = AllowedConnections.isConnectionAllowed(obj as ConnectableObject, this.typedObj, true, "soft"); // no itx
      if (isConnectable) {
        const res = Connection.createConnection(obj as ConnectableObject, this.typedObj);

        if (res.connection) {
          if (!AllowedConnections.isConnectionAllowed(obj as ConnectableObject, this.typedObj, false)) {
            res.connection.forbidden = true;
          }

          return new DropResult(true, res.msg);
        }
      }

      return new DropResult(
        false,
        state.translate("channelTreeViewModel.connectionForbidden", "Verbindung ist nicht erlaubt"),
      );
    } else if ((obj.isChannel || obj.isButtonChannel) && obj.parent?.className == obj.parent?.className) {
      ////
      // move or switch connections from one to other Channel
      ///
      const sourceChannel = obj as Channel;

      // Move Channel (Connection)

      // if TargetChannelType != SourceChannelType
      // Drag&Drop not allowed
      const targetChannel = this.typedObj;
      if (sourceChannel.channelType != ChannelType.Unknown && targetChannel.channelType != ChannelType.Unknown) {
        if (sourceChannel.channelType != targetChannel.channelType) {
          // check first if connected elem can be connected
          let canBeConnected = true;
          sourceChannel.connections.forEach((con) => {
            let obj = con.getPartner(sourceChannel);
            if (obj) {
              if (!AllowedConnections.isConnectionAllowed(targetChannel, obj, true)) {
                canBeConnected = false;
              }
            }
          });
          targetChannel.connections.forEach((con) => {
            let obj = con.getPartner(targetChannel);
            if (obj) {
              if (!AllowedConnections.isConnectionAllowed(sourceChannel, obj, true)) {
                canBeConnected = false;
              }
            }
          });
          if (!canBeConnected) {
            return new DropResult(
              false,
              state.translate("channelTreeViewModel.differentChannelTypes", "Kanaltypen sind unterschiedlich"),
            );
          }
        }
      }

      if (targetChannel.connections.size > 0 && targetChannel.className == "Channel") {
        // buttonchannel should be not in this case
        return new DropResult(
          false,
          state.translate("channelTreeViewModel.channelAlreadyUsed", `Zielkanal ist beleget`),
        );
      }

      sourceChannel.connections.asArray().forEach((connection) => {
        const data = connection.toJSON();
        delete data.id;

        const newConnection = new Connection(data, connection.parent);

        if (connection.parent) {
          (connection.parent as Page).connections.add(newConnection);
        }

        if (connection.fromDevice == sourceChannel) {
          newConnection.fromDevice = targetChannel;
        } else if (connection.toDevice == sourceChannel) {
          newConnection.toDevice = targetChannel;
        }

        newConnection.addToConnectableObjects();
      });

      return new DropResult(true);
    }

    return new DropResult(false);
  }

  connectionsElement(): any {
    const partners: Array<BaseModel> = this.typedObj.getAllConnectedObjects();
    const partnerElements: any = [];

    let fuseElement: any = undefined;

    if (partners.length > 0) {
      partners.forEach((partner) => {
        partnerElements.push(
          <ObjectReference
            className="nav_c2"
            obj={partner}
            relative={this.typedObj.parent as BaseModel}
            onUnlink={() => {
              if (partner.isConnectableObject) {
                this.typedObj.unlinkConnectionWith(partner as ConnectableObject);
              }
            }}
          />,
        );
      });

      if (!this.typedObj.isButtonChannel || this.typedObj.isActorChannel) {
        if (this.typedObj.fuse) {
          fuseElement = (
            <div className="nav_c3">
              <ObjectReference
                obj={this.typedObj.fuse}
                onUnlink={() => {
                  const consumer = this.typedObj.getConnectedConsumer();
                  if (consumer) {
                    return (consumer.fuse = undefined);
                  }
                }}
              />
            </div>
          );
        } else {
          if (this.typedObj.fuse) {
            logger.error("button channel with fuse found");
          }
        }
      }

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            whiteSpace: "nowrap",
            alignItems: "center",
          }}
        >
          <div
            className="nav_C5"
            style={{
              display: "flex",
              flexDirection: "row",
              whiteSpace: "nowrap",
              alignItems: "center",

              marginRight: "10px",
            }}
          >
            {!channelTypeInfo(this.typedObj.channelType).isSupply && (
              <i
                className="las la-link"
                style={{
                  marginTop: "3px",
                  marginRight: "10px",
                  marginLeft: "10px",
                }}
              />
            )}

            {partnerElements}
          </div>
          {fuseElement}
        </div>
      );
    } else {
      return undefined;
    }
  }

  // getButtons(): JSX.Element[] | undefined {
  //   if (channelTypeInfo(this.typedObj.channelType).isSupply) {
  //     const consumer = this.typedObj.getConnectedConsumer();
  //     if (consumer) {
  //       return [super.inlineButton_Reference(consumer)];
  //     }
  //   }
  // }
}
